import React from 'react';
import { ImageFile } from '../types';
import { ArrowLeft, Camera, ImageDown, Layers, Sparkles, Trash2, Zap, Focus, Sun, Waves, Combine, View, Compass, HelpCircle } from 'lucide-react';
import logo from '../assets/logo.gif'; 

interface Props {
  image: ImageFile;
  onBack: () => void;
  onDelete?: (id: string) => void;
}

export default function ImageDetails({ image, onBack, onDelete }: Props) {
  if (!image.quality) return null;

  return (
    <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 relative">
      {/* Logo Background */}
      
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url(${logo})` }}
      ></div>
      
      <div className="relative z-10">
        <div className="p-6 border-b border-[#0CB4E4]/20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Gallery
            </button>
          </div>
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this image?')) {
                  onDelete(image.id);
                  onBack();
                }
              }}
              className="flex items-center px-2 py-2 bg-[#808080] text-white hover:bg-[#808080]/80 rounded-lg transition-colors shadow-sm"
              title="Delete image"
              aria-label="Delete image"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-1/2">
              <img
                src={image.preview}
                alt="Selected image"
                className="w-full rounded-lg shadow-lg"
              />
              <div className="mt-4">
                <h3 className="text-lg font-medium text-white">File Details</h3>
                <dl className="mt-2 text-sm text-gray-300">
                  <div className="flex justify-between py-2 border-b border-[#0CB4E4]/20">
                    <dt>Filename</dt>
                    <dd className="font-medium text-white">{image.file.name}</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#0CB4E4]/20">
                    <dt>Size</dt>
                    <dd className="font-medium text-white">{(image.file.size / 1024 / 1024).toFixed(2)} MB</dd>
                  </div>
                  <div className="flex justify-between py-2 border-b border-[#0CB4E4]/20">
                    <dt>Type</dt>
                    <dd className="font-medium text-white">{image.file.type}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="w-full md:w-1/2">
              <div className="bg-[#0CB4E4]/10 rounded-lg p-4 mb-6 border border-[#0CB4E4]/30">
                <div className="flex items-center justify-between">
                  <span className="text-[#0CB4E4] font-medium">Overall Score</span>
                  <span className="text-2xl font-bold text-[#0CB4E4]">{image.quality.overall}%</span>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Image Quality</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        id: 'lightExposure', 
                        label: 'Light Exposure', 
                        Icon: Sun, 
                        description: 'Evaluates image brightness and exposure. Well-exposed images capture more detail.\n\nTips:\n• Shoot during golden hour\n• Use external lighting\n• Check histogram for clipping\n• Enable HDR if available\n• Use exposure compensation',
                        helpLink: 'https://photographylife.com/understanding-exposure-in-photography'
                      },
                      { 
                        id: 'noise', 
                        label: 'Noise', 
                        Icon: Waves, 
                        description: 'Assesses image grain and artifacts. Lower noise improves feature matching.\n\nTips:\n• Keep ISO as low as possible\n• Use larger aperture (f/1.8-f/4)\n• Ensure good lighting\n• Use noise reduction\n• Consider longer exposure',
                        helpLink: 'https://photographylife.com/what-is-iso-in-photography'
                      }
                    ].map(({ id, label, Icon, description, helpLink }) => (
                      <div key={id} className="p-3 bg-[#161513] rounded-lg border border-[#0CB4E4]/30">
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#0CB4E4]" />
                          <div className="font-medium text-white">{label}</div>
                          <a
                            href={helpLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0CB4E4] hover:text-[#0CB4E4]/80 text-sm underline ml-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            How to improve
                          </a>
                          <div className="group relative">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-300 transition-colors" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 p-4 bg-[#161513] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-pre-line border border-[#0CB4E4]/30">
                              <div className="mb-2">{description?.split('\n\n')[0] || ''}</div>
                              <div className="mt-2 pt-2 border-t border-[#0CB4E4]/30">
                                <div className="font-medium mb-1">{description?.split('\n\n')[1] || ''}</div>
                                {description?.split('\n\n')[2]?.split('\n')?.map((tip, i) => (
                                  <div key={i} className="text-gray-300">{tip}</div>
                                ))}
                              </div>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[#161513] transform rotate-45 border-r border-b border-[#0CB4E4]/30" />
                            </div>
                          </div>
                          <div className={`ml-auto text-sm font-medium ${
                            image.quality[id] >= 80 ? 'text-green-400' :
                            image.quality[id] >= 60 ? 'text-[#0CB4E4]' :
                            'text-[#0CB4E4]'
                          }`}>
                            {image.quality[id]}%
                          </div>
                        </div>
                        {id === 'motionBlur' ? (
                          <div className="w-full bg-[#161513] rounded-full h-1.5 mt-2">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-500 ${
                                image.quality[id] >= 80 ? 'bg-green-400' :
                                image.quality[id] >= 60 ? 'bg-[#0CB4E4]' :
                                'bg-[#0CB4E4]'
                              }`}
                              style={{ width: `${image.quality[id]}%` }}
                            />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Coverage</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        id: 'overlap',
                        label: 'Scene Overlap',
                        Icon: Combine,
                        description: 'Measures how well images overlap. 60-80% overlap is ideal for reconstruction.\n\nHow to improve:\n• Take photos with 60-80% overlap\n• Move in small increments between shots\n• Use grid overlay as guide\n• Follow a systematic pattern\n• Maintain consistent distance'
                      },
                      {
                        id: 'viewpointDiversity',
                        label: 'Viewpoint Diversity',
                        Icon: View,
                        description: 'Evaluates camera position variety. Different angles improve reconstruction accuracy.\n\nHow to improve:\n• Capture from multiple heights\n• Move around the subject in a circle\n• Include oblique angles\n• Vary camera tilt angles\n• Cover all sides thoroughly'
                      }
                    ].map(({ id, label, Icon, description }) => (
                      <div key={id} className="p-3 bg-[#161513] rounded-lg border border-[#0CB4E4]/30">
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-[#0CB4E4]" />
                          <div className="font-medium text-white">{label}</div>
                          <div className="group relative">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-300 transition-colors" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 p-4 bg-[#161513] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-pre-line border border-[#0CB4E4]/30">
                              <div className="mb-2">{description?.split('\n\n')[0] || ''}</div>
                              <div className="mt-2 pt-2 border-t border-[#0CB4E4]/30">
                                <div className="font-medium mb-1">{description?.split('\n\n')[1]?.split(':')[0] || ''}:</div>
                                {description?.split('\n\n')[1]?.split(':')[1]?.split('\n')?.map((tip, i) => (
                                  <div key={i} className="text-gray-300">{tip}</div>
                                ))}
                              </div>
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[#161513] transform rotate-45 border-r border-b border-[#0CB4E4]/30" />
                            </div>
                          </div>
                          <div className={`ml-auto text-sm font-medium ${
                            image.quality[id] >= 80 ? 'text-green-400' :
                            image.quality[id] >= 60 ? 'text-[#0CB4E4]' :
                            'text-[#0CB4E4]'
                          }`}>
                            {image.quality[id]}%
                          </div>
                        </div>
                        <div className="w-full bg-[#161513] rounded-full h-1.5 mt-2">
                          <div
                            className={`h-1.5 rounded-full transition-all duration-500 ${
                              image.quality[id] >= 80 ? 'bg-green-400' :
                              image.quality[id] >= 60 ? 'bg-[#0CB4E4]' :
                              'bg-[#0CB4E4]'
                            }`}
                            style={{ width: `${image.quality[id]}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Camera Settings</h3>
                  <div className="space-y-3">
                    {[
                      { 
                        id: 'focalLength',
                        label: 'Consistent Focal Length',
                        Icon: Camera,
                        description: 'All images should be taken with the same focal length for optimal reconstruction.\n\nHow to improve:\n• Use manual focus mode\n• Avoid zoom during capture\n• Mark zoom position if needed\n• Use prime lens if available\n• Check EXIF data between shots',
                        isBinary: true
                      },
                      {
                        id: 'isLandscape',
                        label: 'Landscape Mode',
                        Icon: Compass,
                        description: 'All images should be taken in landscape orientation for better scene coverage.\n\nHow to improve:\n• Hold camera horizontally\n• Use orientation lock\n• Check viewfinder guides\n• Enable level indicator\n• Watch for rotation warnings',
                        isBinary: true
                      }
                    ].map(({ id, label, Icon, description }) => {
                      const isValid = id === 'isLandscape' ? image.quality.isLandscape : true;
                      
                      return (
                        <div key={id} className="p-3 bg-[#161513] rounded-lg border border-[#0CB4E4]/30">
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5 text-[#0CB4E4]" />
                            <div className="font-medium text-white">{label}</div>
                            <div className="group relative">
                              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-300 transition-colors" />
                              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-80 p-4 bg-[#161513] text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 whitespace-pre-line border border-[#0CB4E4]/30">
                                <div className="mb-2">{description?.split('\n\n')[0] || ''}</div>
                                <div className="mt-2 pt-2 border-t border-[#0CB4E4]/30">
                                  <div className="font-medium mb-1">{description?.split('\n\n')[1]?.split(':')[0] || ''}:</div>
                                  {description?.split('\n\n')[1]?.split(':')[1]?.split('\n')?.map((tip, i) => (
                                    <div key={i} className="text-gray-300">{tip}</div>
                                  ))}
                                </div>
                                <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[#161513] transform rotate-45 border-r border-b border-[#0CB4E4]/30" />
                              </div>
                            </div>
                            <div className={`ml-auto text-sm font-medium ${
                              isValid ? 'text-green-400' : 'text-[#0CB4E4]'
                            }`}>
                              {isValid ? '✓' : '✗'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="p-4 bg-[#0CB4E4]/10 rounded-lg border border-[#0CB4E4]/30">
                  <p className="text-sm text-[#0CB4E4]">
                    <strong>Pro Tip:</strong> For best results, maintain consistent camera settings across all photos and ensure good lighting conditions.
                  </p>
                </div>
              </div>

              {image.quality.details.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-medium text-white mb-2">Detailed Findings</h3>
                  <ul className="space-y-2">
                    {image.quality.details.map((detail, index) => (
                      <li
                        key={index}
                        className={`flex items-center gap-2 text-sm p-2 rounded-lg ${
                          detail.type === 'success' ? 'bg-green-900/20 text-green-400 border border-green-400/30' :
                          detail.type === 'warning' ? 'bg-[#0CB4E4]/10 text-[#0CB4E4] border border-[#0CB4E4]/30' :
                          'bg-[#0CB4E4]/10 text-[#0CB4E4] border border-[#0CB4E4]/30'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${
                          detail.type === 'success' ? 'bg-green-400' :
                          detail.type === 'warning' ? 'bg-[#0CB4E4]' :
                          'bg-[#0CB4E4]'
                        }`} />
                        {detail.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
